import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Laptop, FileClock, Loader2 } from "lucide-react";
import { getMyEquipment, confirmMyAsset } from "@/lib/equipment.functions";
import {
  assetCategoryLabel,
  formatDateOrNull,
  CONFIRMATION_WORDING,
  type EquipmentAsset,
} from "@/lib/equipment";

export const Route = createFileRoute("/_admin/admin/meine-ausstattung")({
  head: () => ({
    meta: [
      { title: "Meine Ausstattung – creategy intern" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MyEquipmentPage,
});

function ConfirmDialog({
  asset,
  onClose,
  onDone,
}: {
  asset: EquipmentAsset;
  onClose: () => void;
  onDone: () => void;
}) {
  const confirmFn = useServerFn(confirmMyAsset);
  const [accepted, setAccepted] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <h2 id="confirm-title" className="font-display text-xl font-extrabold text-primary">
          Erhalt bestätigen
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {assetCategoryLabel(asset.category)}: {asset.label}
        </p>
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1"
          />
          <span>{CONFIRMATION_WORDING}</span>
        </label>
        <label className="mt-4 block text-sm">
          <span className="text-muted-foreground">Mängel oder Hinweise (optional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={3}
            className="mt-1 w-full rounded-md border border-border bg-background p-2"
          />
        </label>
        {err ? <p className="mt-2 text-sm text-destructive">{err}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm">
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!accepted || busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await confirmFn({ data: { id: asset.id, accepted: true, note: note || null } });
                onDone();
              } catch {
                setErr("Die Bestätigung konnte nicht gespeichert werden.");
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-md bg-brand-button px-3 py-2 text-sm font-semibold text-foreground disabled:opacity-50"
          >
            Bestätigen
          </button>
        </div>
      </div>
    </div>
  );
}

function MyEquipmentPage() {
  const loadFn = useServerFn(getMyEquipment);
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<EquipmentAsset | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-equipment"],
    queryFn: () => loadFn(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Ihre Ausstattung wird geladen …
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="text-sm text-destructive">Ausstattung konnte nicht geladen werden.</div>
    );
  }

  const activeAssets = data.assets.filter((a) => !a.returned_at);
  const openCount = activeAssets.filter((a) => !a.confirmed_at).length;

  return (
    <div className="max-w-4xl">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
          Meine Ausstattung
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Geräte und Lizenzen, die Ihnen von der Agentur zur Verfügung gestellt
          wurden. Änderungen klären Sie bitte direkt mit der Geschäftsführung.
        </p>
        {openCount > 0 ? (
          <p className="mt-4 rounded-md border border-border bg-secondary p-3 text-sm">
            Bitte bestätigen Sie den Erhalt von {openCount}{" "}
            {openCount === 1 ? "Gerät" : "Geräten"}.
          </p>
        ) : null}
      </header>

      {confirming ? (
        <ConfirmDialog
          asset={confirming}
          onClose={() => setConfirming(null)}
          onDone={() => {
            setConfirming(null);
            queryClient.invalidateQueries({ queryKey: ["my-equipment"] });
          }}
        />
      ) : null}

      <section className="mb-10">
        <div className="mb-3 flex items-center gap-2">
          <Laptop className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-extrabold">
            Geräte ({activeAssets.length})
          </h2>
        </div>
        {data.assets.length === 0 ? (
          <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
            Ihnen sind derzeit keine Geräte zugewiesen.
          </p>
        ) : (
          <div className="rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Art</th>
                  <th className="px-4 py-3 font-semibold">Bezeichnung</th>
                  <th className="px-4 py-3 font-semibold">Details</th>
                  <th className="px-4 py-3 font-semibold">Ausgegeben</th>
                  <th className="px-4 py-3 font-semibold">Empfang</th>
                </tr>
              </thead>
              <tbody>
                {data.assets.map((asset) => (
                  <tr key={asset.id} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-3">{assetCategoryLabel(asset.category)}</td>
                    <td className="px-4 py-3 font-semibold">
                      {asset.label}
                      {asset.returned_at ? (
                        <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                          zurückgegeben
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-[16rem] truncate px-4 py-3 text-muted-foreground">
                      {asset.details ?? "–"}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{formatDateOrNull(asset.issued_at)}</td>
                    <td className="px-4 py-3">
                      {asset.returned_at ? (
                        "–"
                      ) : asset.confirmed_at ? (
                        <span className="text-muted-foreground">
                          bestätigt am {formatDateOrNull(asset.confirmed_at.slice(0, 10))}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirming(asset)}
                          className="rounded-md bg-brand-button px-3 py-1.5 text-xs font-semibold text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          Erhalt bestätigen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <FileClock className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-extrabold">
            Lizenzen ({data.licenses.length})
          </h2>
        </div>
        {data.licenses.length === 0 ? (
          <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
            Für Ihr Konto sind keine Lizenzen hinterlegt.
          </p>
        ) : (
          <div className="rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Lizenz</th>
                  <th className="px-4 py-3 font-semibold">Anbieter</th>
                  <th className="px-4 py-3 font-semibold">Ablauf</th>
                  <th className="px-4 py-3 font-semibold">Verlängerung</th>
                </tr>
              </thead>
              <tbody>
                {data.licenses.map((license) => {
                  const expired = license.expires_at && license.expires_at < data.today;
                  return (
                    <tr key={license.id} className="border-b border-border last:border-b-0">
                      <td className="px-4 py-3 font-semibold">{license.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{license.vendor ?? "–"}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {formatDateOrNull(license.expires_at)}
                        {expired ? (
                          <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                            abgelaufen
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">{license.auto_renews ? "automatisch" : "manuell"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
